import { DEFAULT_ROUTE, KEYS } from "@/src/const";
import { StrapiComponentType, StrapiType } from "@/src/type";
import { LoadFromCDN, Log, StringHelper } from "@/src/util";

import theme from "@/styles/scss/theme.module.scss";

function addParameters(path: string, parameters: string) {
  if (!parameters) {
    return path;
  }
  return path.includes("?") ? `${path}&${parameters}` : `${path}?${parameters}`;
}

/**
 * A function to construct links
 * @param path - the path that will be linked to
 * @param locale - an override of the locale
 * @param profile - a broker profile slug
 * @param defaultQuery - ONLY USE FOR SSR
 * @returns
 */
function constructLink(path: string, locale?: string, profile?: string | null, defaultQuery?: string, propertySlug?: string, rootPrefix?: string) {
  let __profile: string | undefined | null = profile;
  let __tracking: string | undefined | null = undefined;
  let __cache: string | undefined | null = undefined;
  let __toolkitDomain: string | undefined | null = undefined;
  let searchParams = typeof window === "undefined" && defaultQuery ? new URLSearchParams(`?${defaultQuery}`) : undefined;
  if (typeof window !== "undefined") {
    searchParams = new URLSearchParams(window.location.search);
  }

  // when the path is the link down page, just return path
  if ((path ?? "").startsWith("#")) {
    return path;
  }

  // this makes the property slug is not required to set cta in cms by using `${propertySlug}/available-homes`
  if (propertySlug && (path ?? "").includes("propertySlug")) {
    path = StringHelper.replaceValues(path, { propertySlug });
    if (!path.includes("properties")) {
      path = `properties/${path}`;
    }
  }

  // set the search parmas on the url in a way that works with SSR
  if (searchParams) {
    if (!__profile) {
      if (typeof searchParams.get(KEYS.SEARCH_PARAM.BROKER_PROFILE) === "string") {
        __profile = searchParams.get(KEYS.SEARCH_PARAM.BROKER_PROFILE);
      }
    }

    if (typeof searchParams.get("tracking") === "string") {
      __tracking = searchParams.get("tracking");
    }

    if (typeof searchParams.get("cache") === "string") {
      __cache = searchParams.get("cache");
    }

    if (typeof searchParams.get("toolkitDomain") === "string") {
      __toolkitDomain = searchParams.get("toolkitDomain");
    }
  }

  let __locale: string | undefined | null = locale;
  if (!__locale && typeof window !== "undefined") {
    __locale = document.querySelector("html")?.getAttribute("lang");
  }

  if (__locale === "undefined" || !__locale) {
    __locale = "en";
  }

  if (!path) {
    path = DEFAULT_ROUTE ?? "/home";
    Log.logToProvider(Log.LogLevel.ERROR, "CMSLogic.tsx", "Missing path from link error");
  }

  const carryParams = JSON.parse(
    JSON.stringify({
      profile: __profile ?? undefined,
      tracking: __tracking ?? undefined,
      cache: __cache ?? undefined,
      toolkitDomain: __toolkitDomain ?? undefined,
    })
  );
  const carryParamsStr = new URLSearchParams(carryParams).toString();

  const _rootPrefix = rootPrefix ? `/${rootPrefix}` : "";
  if (path.includes(`${_rootPrefix}/${__locale}`) || (path.length > 1 && path.substring(0, 2) === "/?")) {
    return addParameters(path, carryParamsStr);
  }

  const __path = path.substring(0, 1) === "/" ? path : `/${path}`;
  return addParameters(`${_rootPrefix}/${__locale}${__path}`, carryParamsStr);
}

function changeLocale(currentLocale: string, destinationLocale: string, rootPrefix?: string) {
  if (typeof window !== "undefined") {
    const pathname = window.location.pathname;
    const _rootPrefix = rootPrefix ? `/${rootPrefix}` : "";
    const toCheckPathname = pathname === "/" ? `${_rootPrefix}/en/${DEFAULT_ROUTE}` : pathname;
    const changed = toCheckPathname.replace(`/${currentLocale}/`, `/${destinationLocale}/`);
    return `${changed}${window.location.search ? `${window.location.search}` : ""}`;
  }
  return "/";
}

//eslint-disable-next-line  @typescript-eslint/no-unused-vars
function colorSelector(color?: string, overrideColor?: string, brandColorShade?: string) {
  if (overrideColor) {
    return overrideColor;
  }

  //TODO: add brand color shade
  switch (color ?? "default") {
    case "primaryMain":
      return theme.primaryMain;
    case "primaryLight":
      return theme.primaryLight;
    case "primaryDark":
      return theme.primaryDark;
    case "secondaryMain":
      return theme.secondaryMain;
    case "secondaryLight":
      return theme.secondaryLight;
    case "secondaryDark":
      return theme.secondaryDark;
    case "heart":
      return theme.heart;
    case "ocean":
      return theme.ocean;
    case "olive":
      return theme.olive;
    case "sunlight":
      return theme.sunlight;
    case "roots":
      return theme.roots;
    case "black":
      return theme.black;
    case "white":
      return theme.white;
    case "currentColor":
      return "currentColor";
    default:
      return undefined;
  }
}

function fontWeightSelector(fontWeight?: string) {
  switch (fontWeight ?? "default") {
    case "weight_100":
      return 100;
    case "weight_200":
      return 200;
    case "weight_300":
      return 300;
    case "weight_400":
      return 400;
    case "weight_500":
      return 500;
    case "weight_600":
      return 600;
    case "weight_700":
      return 700;
    case "weight_800":
      return 800;
    case "weight_900":
      return 900;
    default:
      return undefined;
  }
}

function breakpointSelector(breakpoint?: string) {
  switch (breakpoint ?? "default") {
    case "xx":
      return theme.breakpointXX;
    case "xl":
      return theme.breakpointXL;
    case "lg":
      return theme.breakpointLG;
    case "md":
      return theme.breakpointMD;
    case "sm":
      return theme.breakpointSM;
    case "xs":
      return theme.breakpointXS;
    case "ss":
      return theme.breakpointSS;
    default:
      return;
  }
}

function breakpointSelectorNumber(breakpoint?: string, defaultValue?: number) {
  switch (breakpoint ?? "default") {
    case "xx":
      return theme.breakpointXXNum;
    case "xl":
      return theme.breakpointXLNum;
    case "lg":
      return theme.breakpointLGNum;
    case "md":
      return theme.breakpointMDNum;
    case "sm":
      return theme.breakpointSMNum;
    case "xs":
      return theme.breakpointXSNum;
    case "ss":
      return theme.breakpointSSNum;
    default:
      return defaultValue;
  }
}

function generateContainerBreakpointStyle(
  name: string,
  container?: Array<StrapiComponentType.Style.ContainerBreakpoint>,
  layout?: "main" | "alternate" | "split" | "none"
) {
  if (!container || (container ?? []).length === 0) {
    return;
  }

  let style: string = "";
  for (const opt of container) {
    if (!opt.layout || layout === opt.layout) {
      const group = `
        .${name} {
          ${includeInStyle("display", opt.display)}
          ${includeInStyle("flex-direction", opt.flexDirection)}
          ${includeInStyle("justify-content", opt.justifyContent)}
          ${includeInStyle("align-items", opt.alignItems)}
          ${includeInStyle("width", opt.width)}
          ${includeInStyle("height", opt.height)}
          ${includeInStyle("max-width", opt.maxWidth)}
          ${includeInStyle("max-height", opt.maxHeight)}
          ${includeInStyle("min-width", opt.minWidth)}
          ${includeInStyle("min-height", opt.minHeight)}
          ${includeInStyle("padding-top", opt?.padding?.top, opt.padding?.unit)}
          ${includeInStyle("padding-right", opt?.padding?.right, opt.padding?.unit)}
          ${includeInStyle("padding-bottom", opt?.padding?.bottom, opt.padding?.unit)}
          ${includeInStyle("padding-left", opt?.padding?.left, opt.padding?.unit)}
          ${includeInStyle("margin-top", opt?.margin?.top, opt?.margin?.unit)}
          ${includeInStyle("margin-right", opt?.margin?.right, opt?.margin?.unit)}
          ${includeInStyle("margin-bottom", opt?.margin?.bottom, opt?.margin?.unit)}
          ${includeInStyle("margin-left", opt?.margin?.left, opt?.margin?.unit)}
          ${includeInStyle("border-color", colorSelector(opt?.border?.color))}
          ${includeInStyle("border-width", opt?.border?.width, "px")}
          ${includeInStyle("border-style", opt?.border?.color)}
          ${includeInStyle("background-color", colorSelector(opt?.backgroundColor))}
          ${includeInStyle("background", opt?.background)}
      }`;

      if (breakpointSelector(opt.breakpoint)) {
        style += `@media (max-width: ${breakpointSelector(opt.breakpoint)}) {
          ${group}
        }`;
      } else {
        style += group;
      }
    }
  }
  return style.replace(/\n/g, "").replace(/\t/g, "");
}

function generateTextBreakpointStyle(name: string, container?: Array<StrapiComponentType.Element.TextBreakpointProperties>) {
  if (!container || (container ?? []).length === 0) {
    return;
  }

  let style: string = "";
  for (const opt of container) {
    const group = `
        .${name} {
          ${includeInStyle("color", colorSelector(opt?.color, opt?.overrideColor, opt?.brandColorShade))}
          ${includeInStyle("font-family", getFont(opt?.fontFamily))}
          ${includeInStyle("font-size", opt?.fontSize, "pixel")}
          ${includeInStyle("font-weight", fontWeightSelector(opt?.fontWeight))}
          ${includeInStyle("letter-spacing", opt?.letterSpacing)}
          ${includeInStyle("line-height", opt?.lineHeight)}
          ${includeInStyle("text-align", opt?.textAlign)}
          ${includeInStyle("font-style", opt?.fontStyle)}
          ${includeInStyle("text-transform", opt?.textTransform)}
          ${includeInStyle("text-decoration", opt?.textDecoration)}
          ${includeInStyle("padding-top", opt?.padding?.top, opt.padding?.unit)}
          ${includeInStyle("padding-right", opt?.padding?.right, opt.padding?.unit)}
          ${includeInStyle("padding-bottom", opt?.padding?.bottom, opt.padding?.unit)}
          ${includeInStyle("padding-left", opt?.padding?.left, opt.padding?.unit)}
          ${includeInStyle("margin-top", opt?.margin?.top, opt?.margin?.unit)}
          ${includeInStyle("margin-right", opt?.margin?.right, opt?.margin?.unit)}
          ${includeInStyle("margin-bottom", opt?.margin?.bottom, opt?.margin?.unit)}
          ${includeInStyle("margin-left", opt?.margin?.left, opt?.margin?.unit)}
          ${includeInStyle("border-color", colorSelector(opt?.border?.color))}
          ${includeInStyle("border-width", opt?.border?.width, "pixel")}
          ${includeInStyle("border-style", opt?.border?.color)}
          ${includeInStyle("max-width", opt?.maxWidth, "pixel")}
          ${includeInStyle("background", opt?.background)}
          ${opt?.background ? "-webkit-background-clip: text !important;" : ""}
          ${opt?.background ? "-webkit-text-fill-color: transparent !important;" : ""}
      }`;

    if (breakpointSelector(opt.breakpoint)) {
      style += `@media (max-width: ${breakpointSelector(opt.breakpoint)}) {
        ${group}
      }`;
    } else {
      style += group;
    }
  }
  return style.replace(/\n/g, "").replace(/\t/g, "");
}

function generateGapBreakpointStyle(name: string, gap?: Array<StrapiComponentType.Style.GapBreakpoint>) {
  if (!gap || (gap ?? []).length === 0) {
    return;
  }

  let style: string = "";
  for (const opt of gap) {
    const group = `.${name} { 
      ${includeInStyle("gap", opt.gap, "pixel")} 
      ${includeInStyle("justify-content", opt.justifyContent)}
      ${includeInStyle("align-items", opt.alignItems)}
    }`;

    if (breakpointSelector(opt.breakpoint)) {
      style += `@media (max-width: ${breakpointSelector(opt.breakpoint)}) {
        ${group}
      }`;
    } else {
      style += group;
    }
  }
  return style.replace(/\n/g, "").replace(/\t/g, "");
}

function generateAssetBreakpointStyle(name: string, quality: number, assets?: Array<StrapiComponentType.Element.ImageBreakpoint>) {
  if (!assets || (assets ?? []).length === 0) {
    return;
  }

  let style: string = "";
  for (const opt of assets) {
    const url = LoadFromCDN.imageSizeAndQuality(opt?.image?.data?.attributes?.url, quality, breakpointSelectorNumber(opt.breakpoint) ?? 1920);
    const group = `.${name} { 
      ${includeInStyle("background-image", `url(${url})`)} 
      ${includeInStyle("width", opt.width, opt.widthUnit)}
      ${includeInStyle("height", opt.height, opt.heightUnit)}
      ${includeInStyle("background-position", opt.assetPosition)}
      ${includeInStyle("background-size", opt.assetSize)}
      ${includeInStyle("background-repeat", "no-repeat")}
      ${includeInStyle("display", opt.display)}
      ${includeInStyle("justify-content", opt.justifyContent)}
      ${includeInStyle("align-items", opt.alignItems)}
    }`;

    if (breakpointSelector(opt.breakpoint)) {
      style += `@media (max-width: ${breakpointSelector(opt.breakpoint)}) {
        ${group}
      }`;
      //TODO: add height/width properties to available breakpoint creations here
    } else {
      style += group;
    }
  }
  return style.replace(/\n/g, "").replace(/\t/g, "");
}

function generateImageSizedBreakpointStyle(
  name: string,
  breakpoints?: Array<StrapiComponentType.Element.ImageSizedProperties>,
  url?: string,
  useDynamicWidth?: boolean,
  imgTag?: boolean
) {
  if (!breakpoints || (breakpoints ?? []).length === 0) {
    return;
  }

  let style: string = "";
  for (const opt of breakpoints) {
    let backgroundUrlWidth = 1920;

    if (useDynamicWidth) {
      if (typeof opt.width === "number") {
        if (opt.widthUnit === "percent" || opt.widthUnit === "vw") {
          if (opt.width === 100) {
            const bp = parseInt(`${breakpointSelectorNumber(opt.breakpoint, 1920)}`, 10);
            backgroundUrlWidth = bp;
          }
        }

        if (opt.widthUnit === "px") {
          backgroundUrlWidth = opt.width;
        }
      }
    }

    const backgroundUrl = LoadFromCDN.imageSizeAndQuality(url, 100, backgroundUrlWidth ?? 1920);
    const bgImgProps = !imgTag
      ? `
      ${includeInStyle("background-position", opt.assetPosition ?? "center")}
      ${includeInStyle("background-size", opt.assetSize ?? "cover")}
      ${includeInStyle("background-image", `url(${backgroundUrl})`)}
      ${includeInStyle("background-repeat", opt.assetRepeat ?? "no-repeat")}
    `
      : `
      ${includeInStyle("object-fit", opt.assetSize ?? "cover")}
      ${includeInStyle("object-position", opt.assetPosition ?? "center")}
    `;
    const group = `.${name} { 
      ${includeInStyle("position", opt.position)}
      ${includeInStyle("z-index", opt.zIndex)}
      ${includeInStyle("top", opt.top, opt.topUnit)}
      ${includeInStyle("right", opt.right, opt.rightUnit)}
      ${includeInStyle("bottom", opt.bottom ?? opt.botttom, opt.bottomUnit ?? opt.botttomUnit)}
      ${includeInStyle("left", opt.left, opt.leftUnit)}
      ${includeInStyle("width", opt.width, opt.widthUnit)}
      ${includeInStyle("height", opt.height, opt.heightUnit)}
      ${bgImgProps}
    }`;

    if (breakpointSelector(opt.breakpoint)) {
      style += ` @media (max-width: ${breakpointSelector(opt.breakpoint)}) {
        ${group}
      }`;
    } else {
      style += group;
    }
  }
  return style;
}

function translateUnit(unit?: string) {
  switch (unit ?? "default") {
    case "percent":
      return "%";
    case "vw":
      return "vw";
    case "vh":
      return "vh";
    case "px":
    case "pixel":
      return "px";
    default:
      return undefined;
  }
}

function includeInStyle(property: string, value?: string | number, unit?: string) {
  const realUnit = translateUnit(unit);
  return value || `${value}` === "0" ? `${property}: ${value}${realUnit ?? ""} !important;` : "";
}

function createCssValue(value?: string | number, unit?: string) {
  const realUnit = translateUnit(unit);
  return value || `${value}` === "0" ? `${value}${realUnit ?? ""} !important;` : undefined;
}

function tagsToStringArray(tags?: StrapiType.StrapiTags): Array<string> {
  const arr: Array<string> = [];
  if (Array.isArray(tags) && (tags ?? []).length > 0) {
    for (const tag of tags) {
      if (tag.name) {
        arr.push(tag.name);
      }
    }
  }

  return arr;
}

function getFont(fontFamily?: string) {
  switch (fontFamily ?? "default") {
    case "roughwell":
      return "var(--roughwell)";
    case "generation1970Light":
      return "var(--generation1970Light)";
    case "generation1970Outline":
      return "var(--generation1970Outline)";
    case "poppins":
      return "var(--poppins)";
    default:
      return;
  }
}

function isVideo(asset?: StrapiType.StrapiAsset) {
  if (typeof asset?.data?.attributes?.mime !== "string") {
    return;
  }

  const { attributes } = asset?.data;
  if ((attributes?.mime === "video/quicktime" || attributes?.mime === "video/mp4") && attributes?.url) {
    return true;
  }
  return false;
}

function isImage(asset?: StrapiType.StrapiAsset) {
  if (typeof asset?.data?.attributes?.mime !== "string") {
    return;
  }

  const { attributes } = asset?.data;
  if (
    (attributes?.mime === "image/png" ||
      attributes?.mime === "image/jpeg" ||
      attributes?.mime === "image/jpg" ||
      attributes?.mime === "image/avif" ||
      attributes?.mime === "image/webp") &&
    attributes?.url
  ) {
    return true;
  }
  return false;
}

function isPDF(asset?: StrapiType.StrapiAsset) {
  if (typeof asset?.data?.attributes?.mime !== "string") {
    return;
  }

  const { attributes } = asset?.data;
  if (attributes?.mime === "application/pdf" && attributes?.url) {
    return true;
  }
  return false;
}

function imageLinkSelection(image: StrapiType.StrapiAssetAttributes, bestFormat?: keyof StrapiType.StrapiAssetFormat): string {
  if (bestFormat && image?.formats?.[bestFormat]) {
    return image?.formats?.[bestFormat].url;
  }
  return image?.url ?? "";
}

function isMobileBreakPoint(breakpoint?: "xx" | "xl" | "lg" | "md" | "sm" | "xs" | "ss" | "none" | "custom"): boolean {
  if (breakpoint === "md" || breakpoint === "sm" || breakpoint === "xs" || breakpoint === "ss") {
    return true;
  }
  return false;
}

export default {
  constructLink,
  changeLocale,
  colorSelector,
  fontWeightSelector,
  breakpointSelector,
  breakpointSelectorNumber,
  generateContainerBreakpointStyle,
  generateTextBreakpointStyle,
  generateGapBreakpointStyle,
  generateAssetBreakpointStyle,
  generateImageSizedBreakpointStyle,
  tagsToStringArray,
  imageLinkSelection,
  getFont,
  isVideo,
  isImage,
  isPDF,
  includeInStyle,
  translateUnit,
  createCssValue,
  isMobileBreakPoint,
  addParameters,
};
